import { object } from 'yup';
import type { AnySchema } from 'yup';
import type { AnyObject } from 'yup/lib/types';

import type { useManagedIntegrationList } from '@frontend/api';

import { getSchemaFromFeatureFlag } from './const/featureFlagValidators';
import { featureFlagsStaticData } from './const/featureFlags';
import type { useFeatureFlagsQuery } from './data/hooks';
import type { FeatureFlagKey } from './types/FeatureFlag.type';
import type { FeatureFlagFormData } from './types/FeatureFlagFormData.type';

export const getFeatureFlagFormValidationSchema = (
  featureFlagsResponse: ReturnType<typeof useFeatureFlagsQuery>['data'],
  managedIntegrations: ReturnType<typeof useManagedIntegrationList>['data'],
) => {
  const schemaShape = Object.fromEntries(
    Object.entries(featureFlagsStaticData).map(
      ([featureFlagKey, featureFlagStaticData]) => [
        featureFlagKey,
        getSchemaFromFeatureFlag(
          featureFlagStaticData,
          featureFlagsResponse,
          managedIntegrations,
        ),
      ],
    ) as [
      FeatureFlagKey,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      AnySchema<any, AnyObject, FeatureFlagFormData[FeatureFlagKey]>,
    ][],
  );

  return object().shape(schemaShape);
};

const defaultFeatureFlagFormData = Object.fromEntries(
  Object.entries(featureFlagsStaticData).map(
    ([featureFlagKey, featureFlagStaticData]) => [
      featureFlagKey,
      featureFlagStaticData.default,
    ],
  ),
);

export const createFeatureFlagFormData = (
  nonDefaultFeatureFlags?: Partial<FeatureFlagFormData>,
) => ({ ...defaultFeatureFlagFormData, ...nonDefaultFeatureFlags });

export const createReduxStoreStateWithFeatureFlags = (
  nonDefaultFeatureFlags?: Partial<FeatureFlagFormData>,
) => ({
  auth: {
    user: {
      group: {
        featureFlags: createFeatureFlagFormData(nonDefaultFeatureFlags),
      },
    },
  },
});

export const getIsFeatureFlagFormData = (
  value: unknown,
): value is FeatureFlagFormData => {
  if (typeof value === 'object' && value !== null) {
    const schema = getFeatureFlagFormValidationSchema(undefined, undefined);

    try {
      schema.validateSync(value);

      return true;
    } catch (error) {
      // Intentional empty block - swallow error, just return false below.
    }
  }

  return false;
};

export function assertIsFeatureFlagFormData(
  value: unknown,
): asserts value is FeatureFlagFormData {
  // TODO: This should be changed to throw an error in https://paperlessparts.atlassian.net/browse/PP-17821
  if (!getIsFeatureFlagFormData(value)) {
    console.warn(
      'Value passed to assertIsFeatureFlagFormData is not a FeatureFlagFormData object.',
    );
  }
}
